@file:OptIn(ExperimentalTime::class)

package com.macrofocus.eflux.ui

import com.macrofocus.eflux.*
import com.macrofocus.eflux.network.lightning.Lightning
import com.macrofocus.eflux.network.lightning.LightningCluster
import com.macrofocus.eflux.network.line.Line
import com.macrofocus.eflux.network.line.LineCoordinate
import com.macrofocus.eflux.network.risk.RiskComponentPolygon
import com.macrofocus.eflux.network.schema.Schema
import com.macrofocus.eflux.network.substation.Substation
import com.macrofocus.eflux.ui.layer.*
import deckgl.Layer
import js.objects.unsafeJso
import kotlinx.browser.window
import map.*
import mui.material.PaletteMode
import mui.material.styles.Palette
import mui.material.styles.Theme
import mui.material.styles.useTheme
import org.locationtech.jts.geom.Geometry
import org.locationtech.jts.geom.Point
import org.locationtech.jts.geom.Polygon
import org.mkui.react.useProperty
import org.mkui.react.useSelection
import org.mkui.react.useSingleSelection
import org.molap.convert.BasicAutoConverter
import org.molap.dataframe.DataFrame
import org.molap.dataframe.SimpleDataFrame
import org.molap.dataframe.toList
import org.molap.geojson.readGeoJson
import org.molap.json.readJson
import org.molap.questdb.DateTimeHandling
import org.molap.questdb.readQuestDBJson
import react.*
import kotlin.math.pow
import kotlin.math.roundToInt
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

public external interface MorphingMapProps : Props {
    var dataLoader: DataLoader
    var viewLoader: ViewLoader
    var show: Boolean
}

fun zoomFactorInterpolateLinear(x: Double, y1: Double, y2: Double, x1: Double = 8.0, x2: Double = 12.0): Double {
    require(x1 != x2) { "x1 and x2 must be different to avoid division by zero" }
    val x = x.coerceIn(x1, x2)
    return y1 + (x - x1) * (y2 - y1) / (x2 - x1)
}

fun zoomFactorInterpolate(
    x: Double, y1: Double, y2: Double, x1: Double = 8.0, x2: Double = 12.0
): Double {
    require(x1 != x2) { "x1 and x2 must be different to avoid division by zero" }

    val x = x.coerceIn(x1, x2)

    return if (y1 == 0.0 || y2 == 0.0) {
        // If either value is zero, fallback to linear interpolation
        y1 + (x - x1) * (y2 - y1) / (x2 - x1)
    } else {
        // Perform true exponential interpolation
        val factor1 = y1
        val factor2 = y2 / y1
        factor1 * factor2.pow((x - x1) / (x2 - x1))
    }
}

public val MorphingMap = FC<MorphingMapProps> { props ->
    val theme = useTheme<Theme>()

    val (substationNetwork, _) = useProperty(props.dataLoader.substationNetwork)

    val (threeDZoom, _) = useProperty(props.dataLoader.threeDZoom)
    val (zoomLevelLinear, _) = useProperty(props.dataLoader.zoomLevelLinear)

    val (logicalBusProbed, _) = useSingleSelection(props.dataLoader.controller.logicalBusProbing)
    val (logicalBusSelected, _) = useSelection(props.dataLoader.controller.logicalBusSelection)
    val (logicalBranchProbed, _) = useSingleSelection(props.dataLoader.controller.logicalBranchProbing)
    val (logicalBranchSelected, _) = useSelection(props.dataLoader.controller.logicalBranchSelection)
    val (coordinateProbed, _) = useSingleSelection(props.dataLoader.controller.coordinateProbing)
    val (coordinateSelected, _) = useSelection(props.dataLoader.controller.coordinateSelection)

    val (schematic, _) = useProperty(props.dataLoader.schematic)
    val (cartographic, _) = useProperty(props.dataLoader.cartographic)
    val (autoMorphing, _) = useProperty(props.dataLoader.autoMorphing)
    val (startZoomLevelMorphing, _) = useProperty(props.dataLoader.startZoomLevelMorphing)
    val (endZoomLevelMorphing, _) = useProperty(props.dataLoader.endZoomLevelMorphing)
    val (lineWidth, _) = useProperty(props.dataLoader.lineWidth)
    val (riskThreshold, _) = useProperty(props.dataLoader.riskThreshold)
    val (riskBuffer, _) = useProperty(props.dataLoader.riskBuffer)

    val switzerlandFill = theme.asDynamic().switzerlandFill

    val (riskMask, _) = useProperty(props.dataLoader.riskMask)

    var labelsOf by useState<DataFrame<Int, String, Any?>>()
    var substations by useState<Array<Substation>>()
    var lines by useState<Array<Line>>()
    var schemas by useState<Array<Schema>>()
    var lightnings by useState<Array<Lightning>>()
    var lightningClusters by useState<Array<LightningCluster>>()
    var lineCoordinates by useState<Array<LineCoordinate>>()

    val (getSchemas, _) = useProperty(props.dataLoader.schemas)
    val (labelSubstations, _) = useProperty(props.dataLoader.labelSubstations)
    val (animationLinks, _) = useProperty(props.dataLoader.animationLinks)
    val (terrain, _) = useProperty(props.dataLoader.terrain)
    val (getShiftedDateTime, _) = useProperty(props.dataLoader.masterDateTime)
    val shiftedDateTime = getShiftedDateTime.toRawString()

    var styleState by useState<Style>()
    var styleFetched by useState<Style>()

    val substationLastClickTime: RefObject<Double> = useRef<Double>(0.0)
    val lineLastClickTime = useRef<Double>(0.0)
    val coordinateLastClickTime = useRef<Double>(0.0)

    val morphing = useMemo(cartographic) {
//        if(cartographic <= 8) {
//            cartographic.roundToInt().toDouble()
//        } else {
            (cartographic * 10).roundToInt() / 10.0
//        }
//        cartographic
    }
    val isCartographic = useMemo(cartographic) {
        cartographic >= 10.0
    }
    val isMoreCartographic = useMemo(cartographic) {
        cartographic >= 5.0
    }
    val isMoreZoomed = useMemo(threeDZoom, startZoomLevelMorphing, endZoomLevelMorphing) {
        threeDZoom > (startZoomLevelMorphing + endZoomLevelMorphing) / 2.0
    }
    val isCartographicZoomed = useMemo(threeDZoom, endZoomLevelMorphing, isCartographic) {
        threeDZoom >= endZoomLevelMorphing && isCartographic
    }

    useEffectOnce {
//        map.setWorkerUrl("maplibre-gl/dist/maplibre-gl-worker.mjs")
        map.setWorkerUrl("maplibre-gl-worker.mjs")
    }

    useEffectOnce {
        window.fetch("/db/schema").then {
            it.text().then {
                val dataFrame = SimpleDataFrame(DataFrame.readQuestDBJson(it), BasicAutoConverter())
                val list = dataFrame.toList { row ->
                    Schema(
                        getValueAt(row, "schemaId") as Int,
                        getValueAt(row, "logicalGeometry") as Polygon,
                        )
                }
                schemas = list.toTypedArray()
            }
        }
    }
//    val zoomFactor = useMemo(threeDZoom) {
//        zoomFactorInterpolate(threeDZoom, 7.0, 25.0)
//    }

//    useEffect(styleFetched) {
//        style?.findLayer("background")?.let {
//            it.paint!!.unsafeCast<BackgroundPaint>().backgroundColor = "rgba(252, 252, 252, 1)"
//        }
//    }

    useEffect(theme, terrain) {
        val styleUrl = if(terrain) "ch.swisstopo.imagerybasemap.vt/style.json" else  if(theme.palette.unsafeCast<Palette>().mode == PaletteMode.light) "ch.swisstopo.lightbasemap.vt/style.json" else "ch.swisstopo.basemap.vt/style.json"
        window.fetch(styleUrl).then {
            it.json().then {
                if(it != null) {
                    val style = it.unsafeCast<Style>()
                    val layers = style.layers!!

                    layers.find { it.id == "background" }?.let {
                        it.paint.asDynamic()["background-color-transition"] = js("{ duration: 1000, delay: 0 }")
                    }

                    // Switzerland
                    style.addSource("switzerland", unsafeJso<GeoJSONSource> {
                        type = "geojson"
                        data = "switzerland.geojson"
                    })
//                    style.addLayerAfter("background", jso<map.Layer> {
                    val layer = unsafeJso<map.Layer> {
                        id = "switzerland-fill"
                        type = "fill"
                        source = "switzerland"
                    }
                    layer.show(theme)
                    style.addLayer(layer)

                    // OSM
                    style.addSource("osm_power_substation", unsafeJso<VectorSource> {
                        type = "vector"
                        url = "/osm_power_substation"
                    })
                    style.addSource("osm_power_line", unsafeJso<VectorSource> {
                        type = "vector"
                        url = "/osm_power_line"
                    })
                    style.addLayer(unsafeJso<map.Layer> {
                        id = "osm_power_substation"
                        type = "line"
                        source = "osm_power_substation"
                        sourceLayer = "osm_power_substation"
                        minzoom = props.dataLoader.endZoomLevelMorphing.value + 2
                        maxzoom = props.dataLoader.maxZoomLevel.value
                        layout = unsafeJso<dynamic> {
                            visibility = "none"
                        }
                        paint = unsafeJso<LinePaint> {
                            lineColor = "#654321"
                            this.lineWidth = 2.0
                        }
                    })
                    style.addLayer(unsafeJso<map.Layer> {
                        id = "osm_power_line"
                        type = "line"
                        source = "osm_power_line"
                        sourceLayer = "osm_power_line"
                        minzoom = props.dataLoader.endZoomLevelMorphing.value + 2
                        maxzoom = props.dataLoader.maxZoomLevel.value
                        layout = unsafeJso<dynamic> {
                            visibility = "none"
                        }
                        paint = unsafeJso<LinePaint> {
                            lineColor = "#654321"
                            this.lineWidth = 2.0
                        }
                    })

                    // Coordinates
//                    style.addSource("coordinate_weather_at", jso<VectorSource> {
//                        type = "vector"
//                        url = "https://rc.eflux.macrofocus.com/coordinate_weather_at?datetime=2024-06-21 21:35:00"
//                    })
//                    style.addSource("line_power_weather_at", jso<VectorSource> {
//                        type = "vector"
//                        url = "https://rc.eflux.macrofocus.com/line_power_weather_at?datetime=2024-06-21 21:35:00"
//                    })

                    styleFetched = unsafeJso { js("Object").assign(this, style) }
//                style = it.unsafeCast<map.Style?>()
                }
            }
        }
    }

    useEffect(styleFetched, theme, isMoreCartographic, isMoreZoomed, isCartographicZoomed) {
        console.log("Switching to theme ${theme.palette.unsafeCast<Palette>().mode == PaletteMode.light}")

        // Apply settings based on theme
        styleFetched?.let {
            val layers = it.layers!!

            if(theme.palette.unsafeCast<Palette>().mode == PaletteMode.light) {
                layers.find { it.id == "background" }?.let {
                    it.paint!!.unsafeCast<BackgroundPaint>().backgroundColor = theme.asDynamic().backgroundColor
                }
                layers.find { it.id == "switzerland-fill" }?.let {
                    if(isMoreCartographic) {
                        it.hide(theme)
                    } else {
                        it.show(theme)
                    }
                }
                it.findLayer("hillshade_grey")?.let {
                    it.paint!!.unsafeCast<FillPaint>().fillColor = arrayOf(
                        "match",
                        arrayOf("get", "luminosity"),
                        -15, "rgb(200,210,213)",
                        -14, "rgb(203,213,216)",
                        -13, "rgb(207,215,218)",
                        -12, "rgb(210,218,221)",
                        -11, "rgb(213,221,224)",
                        -10, "rgb(217,224,226)",
                        -9, "rgb(220,227,229)",
                        -8, "rgb(224,230,231)",
                        -7, "rgb(227,232,234)",
                        -6, "rgb(231,235,237)",
                        -5, "rgb(234,238,239)",
                        -4, "rgb(238,241,242)",
                        -3, "rgb(241,244,245)",
                        -2, "rgb(245,247,247)",
                        -1, "rgb(248,249,250)",
                        "rgb(252, 252, 252)"
                    )
                }
            } else {
                layers.find { it.id == "background" }?.let {
                    it.paint!!.unsafeCast<BackgroundPaint>().backgroundColor = theme.asDynamic().backgroundColor
                }
                layers.find { it.id == "switzerland-fill" }?.let {
                    if(isMoreCartographic) {
                        it.hide(theme)
                    } else {
                        it.show(theme)
                    }
                }
                it.findLayer("hillshade_grey")?.let {
                    it.paint!!.unsafeCast<FillPaint>().fillColor = arrayOf(
                        "interpolate", arrayOf("linear"), arrayOf("get", "luminosity"), -15, theme.asDynamic().switzerlandFill, -1, theme.asDynamic().backgroundColor
                    )
                }
            }

            // Hide layers that are not needed in the current theme

            console.log("Updating style $isMoreCartographic $isMoreZoomed $isCartographicZoomed")
            try {
                val layers = it.layers!!

                if(isMoreCartographic) {
                    if(isMoreZoomed) {
                        if(isCartographicZoomed) {
                            val layersToHide = arrayOf("switzerland-fill")
                            layers.forEach { layer ->
                                if (layersToHide.contains(layer.id)) {
                                    layer.hide(theme)
                                } else {
                                    layer.show(theme)
                                }
                            }
                        } else {
                            val layersToShow = arrayOf("background", "boundary", "hillshade_grey", "hillshade_yellow", "swissimage")
                            layers.forEach { layer ->
                                if (layersToShow.contains(layer.id)) {
                                    layer.show(theme)
                                } else {
                                    layer.hide(theme)
                                }
                            }
                        }
                    } else {
                        val layersToShow =
                            arrayOf("background", "boundary", "hillshade_grey", "hillshade_yellow", "swissimage")
                        layers.forEach { layer ->
                            if (layersToShow.contains(layer.id)) {
                                layer.show(theme)
                            } else {
                                layer.hide(theme)
                            }
                        }
                    }
                } else {
                    val layersToKeep = arrayOf("background", "switzerland-fill")
                    layers.forEach { layer ->
                        if (!layersToKeep.contains(layer.id)) {
                            layer.hide(theme)
                        } else {
                            layer.show(theme)
                        }
                    }
                }
            } catch (e: Throwable) {
                println(e)
            }

            styleState = unsafeJso { js("Object").assign(this, it) }
        }
    }

    useEffect(shiftedDateTime, substationNetwork, schematic) {
        substations = substationNetwork?.nodes?.toList()?.toTypedArray()
    }

    useEffect(shiftedDateTime, substationNetwork, schematic) {
        lines = substationNetwork?.links?.toList()?.toTypedArray()
    }

    useEffect(schematic) {
        window.fetch("data/labels-${schematic}.json").then {
            it.text().then {
                val dataFrame = SimpleDataFrame(DataFrame.readJson(it), BasicAutoConverter())
//                val dataFrame = SimpleDataFrame(DataFrame.readQuestDBJson(it), BasicAutoConverter())
                labelsOf = dataFrame
            }
        }
    }

    useEffect(shiftedDateTime) {
        window.fetch("db/lightning/${shiftedDateTime}").then {
            it.text().then {
                val dataFrame = DataFrame.readQuestDBJson(it, DateTimeHandling.Instant, "geometry")
                val list = dataFrame.toList { row ->
                    Lightning(
                        getValueAt(row, "stroke_time") as Instant,
                        getValueAt(row, "stroke_current") as? Double,
                        getValueAt(row, "lightning_type") as Int,
                        getValueAt(row, "geometry") as Point
                    )
                }
                lightnings = list.toTypedArray()
            }
        }
    }

    useEffect(shiftedDateTime) {
        window.fetch("data/lightningClusters/${shiftedDateTime}").then {
            it.text().then {
                val dataFrame = DataFrame.readGeoJson(it)
                val list = dataFrame.toList { row ->
                    LightningCluster(
                        getValueAt(row, "risk_time") as String,
                        getValueAt(row, "cluster_id") as String,
                        getValueAt(row, "order") as Int,
                        getValueAt(row, "type") as String,
                        getValueAt(row, "geometry") as? Geometry?,
                        getValueAt(row, "area") as? Double?,
                        getValueAt(row, "strike_count") as Int,

                    )
                }
                lightningClusters = list.toTypedArray()
            }
        }
    }

    useEffect(shiftedDateTime) {
        window.fetch("/db/lineCoordinates/${shiftedDateTime}").then {
            it.text().then {
                val dataFrame = DataFrame.readQuestDBJson(it, DateTimeHandling.Instant, "logicalGeometry", "physicalGeometry")
                val list = dataFrame.toList { row ->
                    LineCoordinate(
                        getValueAt(row, "logicalBranchId") as String,
                        getValueAt(row, "seq") as Int,
                        getValueAt(row, "coordinateId") as String,
                        getValueAt(row, "logicalGeometry") as Geometry,
                        getValueAt(row, "physicalGeometry") as Geometry,
                        getValueAt(row, "risk_map") as Int,
                        getValueAt(row, "risk_map_hex") as? String,
                        getValueAt(row, "risk_list") as String,
                        getValueAt(row, "logicalBranchIds") as String,
                        getValueAt(row, "probability") as? Double,
                        getValueAt(row, "details") as? String,
                        getValueAt(row, "description") as? String,
                        getValueAt(row, "t_70m") as? Double,
                        getValueAt(row, "wind_speed_70m") as? Double,
                        getValueAt(row, "wind_gusts_70m_1h") as? Double,
                        getValueAt(row, "precip_5min") as? Double,
                        getValueAt(row, "precip_type_intensity_5min") as? Double,
                        getValueAt(row, "forest_fire_warning") as? Double,
                        getValueAt(row, "power_line_oscillation_24h") as? Double,
                        getValueAt(row, "weather_code_1h") as? Double,
                        getValueAt(row, "tstorm_warning_1h") as? Double,
                        getValueAt(row, "air_density_70m") as? Double,
                    )
                }
                lineCoordinates = list.toTypedArray()
            }
        }
    }

    val preLayers = ArrayList<Layer>()
    val postLayers = ArrayList<Layer>()

    val schemaLayer = useMemo(getSchemas, schemas, morphing, theme, isCartographicZoomed) {
        createSchemaLayer(props.dataLoader, schemas, morphing, theme, isCartographicZoomed, getSchemas)
    }
    preLayers.add(schemaLayer)

    val substationLayer = useMemo(substations, morphing, lineWidth, theme, isCartographicZoomed, logicalBusProbed) {
        createSubstationLayer(props.dataLoader, substations, morphing, lineWidth, theme, isCartographicZoomed, logicalBusProbed)
    }
    postLayers.add(substationLayer)
    val labelLayer = useMemo(labelSubstations, labelsOf, morphing, theme, isCartographicZoomed) {
        createLabelLayer(props.dataLoader, labelSubstations, labelsOf, morphing, theme, isCartographicZoomed)
    }
    postLayers.add(labelLayer)

    val riskComponentPolygons: Array<RiskComponentPolygon> = useMemo(substationNetwork, lines, riskThreshold, riskBuffer, riskMask, isMoreCartographic, isCartographicZoomed) {
        val componentList = ArrayList<RiskComponentPolygon>()
        val components = substationNetwork?.riskComponentsCapability?.riskComponents(lines, riskThreshold, riskBuffer, riskMask, isMoreCartographic, isCartographicZoomed) ?: emptyList()
        for (component in components) {
            for (g  in component.polygon) {
                componentList.add(g)
            }
        }
        componentList.toTypedArray()
    }
    val riskComponentLayer = useMemo(riskComponentPolygons, riskMask, isCartographicZoomed) {
        createRiskComponentLayer(riskComponentPolygons, isCartographicZoomed, riskMask,
            props.dataLoader.riskComponentProbing, props.dataLoader.riskComponentSelection)
    }
    preLayers.add(riskComponentLayer)

    val interactiveSubstationLayer = useMemo(substations, morphing, lineWidth, theme, isCartographicZoomed, logicalBusProbed, logicalBusSelected) {
        createInteractiveSubstationLayer(props.dataLoader, substations, morphing, lineWidth, theme, isCartographicZoomed, logicalBusProbed, logicalBusSelected, substationLastClickTime)
    }
    preLayers.add(interactiveSubstationLayer)

    val interactiveLineLayer = useMemo(lines, morphing, lineWidth, theme, logicalBranchProbed, logicalBranchSelected) {
        createInteractiveLineLayer(props.dataLoader, lines, morphing, lineWidth, theme, logicalBranchProbed, logicalBranchSelected, lineLastClickTime)
    }
    preLayers.add(interactiveLineLayer)

    val lineLayer = useMemo(lines, morphing, lineWidth, theme) {
        createLineLayer(props.dataLoader, lines, morphing, lineWidth, theme)
    }
    postLayers.add(lineLayer)

    val lighningClustersLayer = useMemo(lightningClusters, isCartographic) {
        createLightningClustersLayer(lightningClusters, isCartographic, null, null)
    }
    preLayers.add(lighningClustersLayer)
    val coordinateLayer = useMemo(getShiftedDateTime, riskMask, isCartographicZoomed, zoomLevelLinear, isCartographic) {
        createCoordinateLayer(props.dataLoader, getShiftedDateTime, riskMask, isCartographicZoomed, zoomLevelLinear, isCartographic, coordinateLastClickTime)
    }
    postLayers.add(coordinateLayer)
    val lighningLayer = useMemo(lightnings, isCartographic) {
        createLightningLayer(props.dataLoader, lightnings, cartographic, theme, isCartographicZoomed, isCartographic)
    }
    postLayers.add(lighningLayer)
    val lineCoordinatesLayer = useMemo(lineCoordinates, cartographic, isCartographic, riskMask, coordinateProbed, coordinateSelected) {
        createLineCoordinatesLayer(props.dataLoader, lineCoordinates, cartographic, riskMask, theme, isCartographicZoomed, isCartographic, coordinateProbed, coordinateSelected, coordinateLastClickTime)
    }
    postLayers.add(lineCoordinatesLayer)

    if (lines == null) {
        return@FC
    }

    if (!props.show) {
        return@FC
    }

    AnimatedDeckGL {
        this.dataLoader = props.dataLoader
        this.mapStyle = styleState
        this.preLayers = preLayers
        this.postLayers = postLayers
        this.lines = lines
        this.morphing = morphing
        this.animate = animationLinks
        this.animateWhileZooming = false
    }
}
